import { action, atom, isDeepEqual } from '@reatom/framework'
import i18next from 'i18next'

import { pipe, Record } from '@blade/std-lib'
import { showSnackbarAction } from '@root/shared/ui-kit'
import { notificationWidgetsLayoutAtom } from '@root/widgets/notification-widgets/atoms'

import { filterWidgetRows, toWidgetRows, widgetRowsVariants } from '../model'

import type { WidgetRows } from '../model'
import type {
  NotificationCenterSharedWidget,
  NotificationCenterWidget,
  NotificationWidget,
  NotificationWidgetVariant,
} from '@root/widgets/notification-widgets/model'

/** Everything the settings screen can edit, kept together to tell drafts apart. */
type NotificationCenterSettings = {
  personalOrder: Array<NotificationWidget>
  /** Mirrors notificationWidgetsLayoutAtom: squares share a row, rows span it. */
  personalVariants: Record<NotificationWidget, NotificationWidgetVariant>
  sharedOrder: Array<NotificationCenterSharedWidget>
  additionalWidgetsView: NotificationWidgetVariant
  visibility: Record<NotificationCenterWidget, boolean>
}

const DEFAULT_SETTINGS: NotificationCenterSettings = {
  personalOrder: ['profile', 'wallets', 'txs'],
  personalVariants: { profile: 'square', wallets: 'square', txs: 'row' },
  sharedOrder: ['walletTracker', 'premium', 'notifications', 'support'],
  additionalWidgetsView: 'row',
  visibility: {
    profile: true,
    wallets: true,
    txs: true,
    walletTracker: false,
    premium: false,
    notifications: false,
    support: false,
  },
}

export const notificationCenterPersonalWidgetsOrderAtom = atom(
  DEFAULT_SETTINGS.personalOrder,
  'notificationCenterPersonalWidgetsOrderAtom',
)

export const notificationCenterPersonalWidgetsVariantsAtom = atom(
  DEFAULT_SETTINGS.personalVariants,
  'notificationCenterPersonalWidgetsVariantsAtom',
)

export const setNotificationCenterPersonalWidgetsRowsAction = action(
  (ctx, rows: WidgetRows<NotificationWidget>) => {
    notificationCenterPersonalWidgetsOrderAtom(ctx, rows.flat())
    notificationCenterPersonalWidgetsVariantsAtom(ctx, widgetRowsVariants(rows))
  },
  'setNotificationCenterPersonalWidgetsRowsAction',
)

export const notificationCenterSharedWidgetsOrderAtom = atom(
  DEFAULT_SETTINGS.sharedOrder,
  'notificationCenterSharedWidgetsOrderAtom',
)

export const notificationCenterAdditionalWidgetsViewAtom = atom(
  DEFAULT_SETTINGS.additionalWidgetsView,
  'notificationCenterAdditionalWidgetsViewAtom',
)

export const notificationCenterWidgetsVisibilityAtom = atom(
  DEFAULT_SETTINGS.visibility,
  'notificationCenterWidgetsVisibilityAtom',
)

export const toggleNotificationCenterWidgetAction = action(
  (ctx, widget: NotificationCenterWidget) => {
    notificationCenterWidgetsVisibilityAtom(ctx, (visibility) =>
      pipe(visibility, Record.set(widget, !visibility[widget])),
    )
  },
  'toggleNotificationCenterWidgetAction',
)

const notificationCenterSettingsAtom = atom(
  (ctx): NotificationCenterSettings => ({
    personalOrder: ctx.spy(notificationCenterPersonalWidgetsOrderAtom),
    personalVariants: ctx.spy(notificationCenterPersonalWidgetsVariantsAtom),
    sharedOrder: ctx.spy(notificationCenterSharedWidgetsOrderAtom),
    additionalWidgetsView: ctx.spy(notificationCenterAdditionalWidgetsViewAtom),
    visibility: ctx.spy(notificationCenterWidgetsVisibilityAtom),
  }),
  'notificationCenterSettingsAtom',
)

const savedNotificationCenterSettingsAtom = atom(
  DEFAULT_SETTINGS,
  'savedNotificationCenterSettingsAtom',
)

/** Compares values, so moving a widget away and back counts as no change. */
export const notificationCenterSettingsChangedAtom = atom(
  (ctx) =>
    !isDeepEqual(
      ctx.spy(notificationCenterSettingsAtom),
      ctx.spy(savedNotificationCenterSettingsAtom),
    ),
  'notificationCenterSettingsChangedAtom',
)

/**
 * Publishes the edited rows to the widgets view. Hidden widgets are left out,
 * which can leave a half-width widget alone in its row, so the variants are
 * derived from the rows that survive rather than copied from the draft.
 */
export const saveNotificationCenterSettingsAction = action((ctx) => {
  const visibility = ctx.get(notificationCenterWidgetsVisibilityAtom)
  const rows = filterWidgetRows(
    toWidgetRows(
      ctx.get(notificationCenterPersonalWidgetsOrderAtom),
      ctx.get(notificationCenterPersonalWidgetsVariantsAtom),
    ),
    (widget) => visibility[widget],
  )
  const variants = widgetRowsVariants(rows)

  notificationWidgetsLayoutAtom(
    ctx,
    rows.flat().map((widget, order) => ({
      widget,
      variant: variants[widget],
      order,
    })),
  )
  savedNotificationCenterSettingsAtom(
    ctx,
    ctx.get(notificationCenterSettingsAtom),
  )
  showSnackbarAction(ctx, {
    text: i18next.t('notification-center.widgets_settings_changed'),
    intent: 'success',
  })
}, 'saveNotificationCenterSettingsAction')

/** Restores drafts from the last saved snapshot (or defaults). */
export const resetNotificationCenterSettingsAction = action((ctx) => {
  const saved = ctx.get(savedNotificationCenterSettingsAtom)

  notificationCenterPersonalWidgetsOrderAtom(ctx, saved.personalOrder)
  notificationCenterPersonalWidgetsVariantsAtom(ctx, saved.personalVariants)
  notificationCenterSharedWidgetsOrderAtom(ctx, saved.sharedOrder)
  notificationCenterAdditionalWidgetsViewAtom(ctx, saved.additionalWidgetsView)
  notificationCenterWidgetsVisibilityAtom(ctx, saved.visibility)
}, 'resetNotificationCenterSettingsAction')

export const refreshNotificationCenterWidgetAction = action(
  (_ctx, widget: NotificationCenterWidget) => {
    // TODO: refetch the widget data, subscribers can react via onCall
    return widget
  },
  'refreshNotificationCenterWidgetAction',
)
